/**
 * Decide — production page family (WP6b / #46626 owns QueuePage only).
 *
 * Review-queue controls are real actions with `data-action-id`. Demo and Real
 * both use the WP4a verificationShell contracts. No Prototype: toasts.
 * WP6c owns FindingsPage + FindingDetailPage. WP6d owns RunsPage + RunDetailPage:
 * replay run and open receipt from run, with data-action-id bindings. Do not
 * treat findings/inbox copy as this slice.
 */

import { useEffect, useMemo, useState } from "react";
import { formatDateTime, formatRelativeTime } from "@excellent/ui";
import { useApi, ApiError } from "@excellent/ui/api";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import type {
  VerificationFindingDto,
  VerificationRunDto,
} from "@excellent/ui/api/contracts/verification-shell";
import { clusterFindings } from "@excellent/core/verification-finding-clusters-client";
import { draftRationale, signRationale } from "@excellent/core/verification-rationale-drafts";
import {
  projectUniversalCompletionOverview,
  type UniversalConsoleOverview,
} from "@excellent/core/verification-universal-surfaces-client";
import {
  ReadOnlyFailureCallout,
  readOnlyFailureNoticeFromError,
  type ActionNotice,
} from "../read-only-errors";
import { useVerificationEnvOptional } from "../env-provider";
import { stripEnvironmentPrefix, type VerificationEnvironment } from "../env";
import { href } from "../nav";
import { resolvedVerdictOf } from "../resolved-verdicts";
import { openVerificationSearch } from "../search-palette";
import {
  ActionButton,
  ButtonRow,
  Callout,
  Card,
  CardContent,
  DataTable,
  Field,
  Fields,
  FindingCard,
  FilterField,
  FilterToolbar,
  Label,
  ListSearch,
  ActionMenu,
  PageActions,
  Mono,
  ScopeTabs,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Severity,
  Stack,
  Textarea,
  ToneBadge,
  Verdict,
  type DataTableColumn,
} from "../ui";
import {
  PageError,
  PageFrame,
  PageLink,
  PagePending,
  type VerificationDetailPageProps,
  type VerificationPageProps,
} from "./page-frame";
import {
  verificationCanonicalClients,
  verificationSingleFlightMutation,
} from "../api/canonical-clients";
import {
  combineCompletionHistory,
  currentAuthorityIds,
  isRetrospectiveHistoryReceipt,
} from "../completion-history";

const ACTION = {
  scopeMine: "queue.scope-mine",
  scopeAll: "queue.scope-all",
  scopeUnread: "queue.scope-unread",
  scopeDone: "queue.scope-done",
  filterVerdict: "queue.filter-verdict",
  filterSeverity: "queue.filter-severity",
  sort: "queue.sort",
  markRead: "queue.mark-read",
  markUnread: "queue.mark-unread",
  open: "queue.open-and-decide",
  accept: "queue.accept",
  reject: "queue.reject",
  reassign: "queue.reassign",
  escalate: "queue.escalate",
  reVerify: "queue.re-verify",
  more: "queue.more",
} as const;

const FINDINGS_ACTION = {
  search: "findings.search",
  filterStatus: "findings.filter-status",
  filterStatusAll: "findings.filter-status-all",
  filterStatusClosed: "findings.filter-status-closed",
  filterSeverity: "findings.filter-severity",
  openRow: "findings.open-row",
  accept: "findings.accept",
  reject: "findings.reject",
  sign: "findings.sign-rationale",
  openEvidence: "findings.open-evidence",
  recordedRationale: "findings.recorded-rationale",
} as const;

export const RUNS_REPLAY_ACTION = "runs.replay";
export const RUNS_OPEN_RECEIPT_ACTION = "runs.open-receipt";

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const QUEUE_RATIONALE_REASON = "Open the finding and write a rationale first.";

type QueueScope = "mine" | "all" | "unread" | "done";
type QueueSort = "priority" | "newest" | "oldest" | "severity" | "updated";
type QueueVerdictFilter = "all" | "FAIL" | "UNKNOWN";
type QueueSeverityFilter = "all" | "critical" | "high" | "medium" | "low";

function initialQueueScope(): QueueScope {
  if (typeof window === "undefined") return "mine";
  const value = new URLSearchParams(window.location.search).get("status");
  return value === "all" || value === "open"
    ? "all"
    : value === "unread" || value === "done" || value === "mine"
      ? value
      : "mine";
}

function initialQueueSort(): QueueSort {
  if (typeof window === "undefined") return "priority";
  const value = new URLSearchParams(window.location.search).get("sort");
  return value === "newest" || value === "oldest" || value === "severity" || value === "updated"
    ? value
    : "priority";
}

function initialQueueVerdict(): QueueVerdictFilter {
  if (typeof window === "undefined") return "all";
  const value = new URLSearchParams(window.location.search).get("verdict");
  return value === "FAIL" || value === "UNKNOWN" ? value : "all";
}

function initialQueueSeverity(): QueueSeverityFilter {
  if (typeof window === "undefined") return "all";
  const value = new URLSearchParams(window.location.search).get("severity");
  return value === "critical" || value === "high" || value === "medium" || value === "low"
    ? value
    : "all";
}

function initialQueueSearch(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("q") ?? "";
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "The action did not complete.";
}

function environmentActor(environment: VerificationEnvironment): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_matt";
}

function inboxReadStorageKey(environment: VerificationEnvironment, actorId: string): string {
  return `excellent.verification.inbox.read:${environment}:${actorId}`;
}

function readInboxReadIds(environment: VerificationEnvironment, actorId: string): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(inboxReadStorageKey(environment, actorId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [],
    );
  } catch {
    return new Set();
  }
}

function writeInboxReadIds(
  environment: VerificationEnvironment,
  actorId: string,
  ids: Set<string>,
) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    inboxReadStorageKey(environment, actorId),
    JSON.stringify([...ids].sort()),
  );
}

function worstFirst(items: VerificationFindingDto[]): VerificationFindingDto[] {
  return [...items].sort((a, b) => {
    const sev = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
    if (sev !== 0) return sev;
    return Date.parse(a.openedAt) - Date.parse(b.openedAt);
  });
}

function sortInboxItems(
  items: VerificationFindingDto[],
  sort: QueueSort,
): VerificationFindingDto[] {
  return [...items].sort((a, b) => {
    if (sort === "newest") return Date.parse(b.openedAt) - Date.parse(a.openedAt);
    if (sort === "oldest") return Date.parse(a.openedAt) - Date.parse(b.openedAt);
    if (sort === "severity") {
      const sev = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
      if (sev !== 0) return sev;
      return Date.parse(a.openedAt) - Date.parse(b.openedAt);
    }
    if (sort === "updated") return Date.parse(b.openedAt) - Date.parse(a.openedAt);
    const status = Number(a.status !== "open") - Number(b.status !== "open");
    if (status !== 0) return status;
    const verdict =
      Number(a.verificationStatus !== "FAIL") - Number(b.verificationStatus !== "FAIL");
    if (verdict !== 0) return verdict;
    const sev = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
    if (sev !== 0) return sev;
    return Date.parse(a.openedAt) - Date.parse(b.openedAt);
  });
}

function publicFindingParam(finding: { id: string; ref: string }): string {
  return finding.id || finding.ref;
}

function findingMatchesParam(finding: { id: string; ref: string }, raw: string): boolean {
  const id = decodeURIComponent(raw);
  if (finding.id === id || finding.ref === id) return true;
  const stripped = stripEnvironmentPrefix(finding.id);
  if (stripped === id) return true;
  const fromRef = finding.ref.replace(/^F-/i, "fnd_");
  return stripped === fromRef || stripped.endsWith(fromRef);
}

function observationLabel(ref: string, artifacts: { id: string; label: string }[]): string {
  const stripped = stripEnvironmentPrefix(ref);
  const hash = stripped.indexOf("#");
  const artifactId = hash >= 0 ? stripped.slice(0, hash) : stripped;
  const fragment = hash >= 0 ? stripped.slice(hash + 1) : "";
  const row = fragment.startsWith("row/") ? fragment.slice(4) : fragment;
  const artifact = artifacts.find(
    (item) =>
      item.id === artifactId ||
      stripEnvironmentPrefix(item.id) === artifactId ||
      item.id.endsWith(artifactId),
  );
  const name = artifact?.label ?? artifactId.replace(/_/g, " ");
  return row ? `${name} · ${row}` : name;
}

function clusterCopy(
  clusters: { id: string; findingIds: string[] }[],
  rows: VerificationFindingDto[],
): { title: string; body: string } {
  const sharing = clusters.filter((cluster) => cluster.findingIds.length > 1);
  if (sharing.length === 0) {
    return {
      title: `${clusters.length} distinct cause${clusters.length === 1 ? "" : "s"}`,
      body: "None of these findings share a root cause. Treat each row as its own decision.",
    };
  }
  return {
    title: `${sharing.length} shared cause${sharing.length === 1 ? "" : "s"}`,
    body: sharing
      .map((cluster) => {
        const sample = rows.find((row) => cluster.findingIds.includes(row.id));
        return sample
          ? `${cluster.findingIds.length} findings share a cause on ${sample.title}.`
          : `${cluster.findingIds.length} findings share one cause.`;
      })
      .join(" "),
  };
}

const FINDINGS_COLUMNS: DataTableColumn<VerificationFindingDto>[] = [
  {
    key: "finding",
    header: "Finding",
    cellClassName: "whitespace-normal",
    render: (finding) => (
      <div className="flex flex-col gap-1">
        <span className="font-medium text-foreground">{finding.title}</span>
        <Mono>{finding.ref}</Mono>
      </div>
    ),
  },
  {
    key: "result",
    header: "Result",
    render: (finding) => <Verdict status={finding.verificationStatus} />,
  },
  {
    key: "severity",
    header: "Severity",
    render: (finding) => <Severity level={finding.severity} />,
  },
  {
    key: "status",
    header: "Status",
    render: (finding) => (
      <ToneBadge tone={finding.status === "open" ? "neutral" : "pass"}>{finding.status}</ToneBadge>
    ),
  },
  {
    key: "opened",
    header: "Opened",
    render: (finding) => formatRelativeTime(finding.openedAt),
  },
];

export function QueuePage({ onNavigate }: VerificationPageProps) {
  return <QueueBody onNavigate={onNavigate} />;
}

export function InboxPage({ onNavigate }: VerificationPageProps) {
  return <QueueBody onNavigate={onNavigate} />;
}

function QueueBody({ onNavigate }: VerificationPageProps) {
  const envCtx = useVerificationEnvOptional();
  const environment: VerificationEnvironment = envCtx?.environment ?? "demo";
  const envReady = envCtx ? envCtx.ready && envCtx.environment !== null : true;
  const me = environmentActor(environment);

  const [scope, setScope] = useState<QueueScope>(initialQueueScope);
  const [sort, setSort] = useState<QueueSort>(initialQueueSort);
  const [verdict, setVerdict] = useState<QueueVerdictFilter>(initialQueueVerdict);
  const [severity, setSeverity] = useState<QueueSeverityFilter>(initialQueueSeverity);
  const [q, setQ] = useState(initialQueueSearch);
  const [readIds, setReadIds] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const queueApi = useApi(
    verificationCanonicalClients.inbox.operation,
    envReady ? { query: { env: environment, actorId: me } } : null,
  );
  const universalApi = useApi(
    universalVerification.overview,
    envReady && environment === "real" ? {} : null,
  );
  const completionProjection = useMemo(
    () => (universalApi.data ? projectUniversalCompletionOverview(universalApi.data) : null),
    [universalApi.data],
  );
  const currentReviewIds = useMemo(
    () => new Set(completionProjection?.current.reviews.map((review) => review.request.id) ?? []),
    [completionProjection],
  );

  const items = useMemo(() => {
    if (!envReady) return [];
    return queueApi.data?.items ?? [];
  }, [envReady, queueApi.data]);

  const isUnread = (finding: VerificationFindingDto) =>
    finding.status === "open" && !readIds.has(finding.id);
  const mine = items.filter(
    (finding) =>
      finding.status === "open" && (finding.assignedActorId === me || finding.ownerActorId === me),
  );
  const all = items.filter((finding) => finding.status === "open");
  const unread = items.filter(isUnread);
  const done = items.filter((finding) => finding.status !== "open");
  const scoped =
    scope === "mine" ? mine : scope === "all" ? all : scope === "unread" ? unread : done;
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let filtered = scoped;
    if (verdict !== "all")
      filtered = filtered.filter((finding) => finding.verificationStatus === verdict);
    if (severity !== "all") filtered = filtered.filter((finding) => finding.severity === severity);
    if (needle) {
      filtered = filtered.filter(
        (finding) =>
          finding.title.toLowerCase().includes(needle) ||
          finding.ref.toLowerCase().includes(needle) ||
          finding.id.toLowerCase().includes(needle) ||
          finding.summary.toLowerCase().includes(needle) ||
          finding.ask.toLowerCase().includes(needle) ||
          finding.controlId.toLowerCase().includes(needle) ||
          finding.runId.toLowerCase().includes(needle) ||
          (finding.assignedActorId ?? "").toLowerCase().includes(needle) ||
          (finding.ownerActorId ?? "").toLowerCase().includes(needle) ||
          finding.evidenceArtifactIds.some((id) => id.toLowerCase().includes(needle)) ||
          finding.citationRefs.some((ref) => ref.toLowerCase().includes(needle)),
      );
    }
    return sortInboxItems(filtered, sort);
  }, [q, scoped, severity, sort, verdict]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = list.find((finding) => finding.id === selectedId) ?? list[0] ?? null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    q ? params.set("q", q) : params.delete("q");
    scope !== "mine" ? params.set("status", scope) : params.delete("status");
    sort !== "priority" ? params.set("sort", sort) : params.delete("sort");
    verdict !== "all" ? params.set("verdict", verdict) : params.delete("verdict");
    severity !== "all" ? params.set("severity", severity) : params.delete("severity");
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(window.history.state, "", next);
    }
  }, [q, scope, severity, sort, verdict]);

  useEffect(() => {
    if (!selectedId || list.some((finding) => finding.id === selectedId)) return;
    setSelectedId(list[0]?.id ?? null);
  }, [list, selectedId]);

  useEffect(() => {
    setReadIds(readInboxReadIds(environment, me));
  }, [environment, me]);

  function markRead(id: string) {
    setReadIds((current) => {
      const next = new Set(current).add(id);
      writeInboxReadIds(environment, me, next);
      return next;
    });
  }

  function markUnread(id: string) {
    setReadIds((current) => {
      const next = new Set(current);
      next.delete(id);
      writeInboxReadIds(environment, me, next);
      return next;
    });
  }

  const reassignReason = "Reassign needs a WP4 actor-assign contract.";
  const escalateReason = "Escalate needs a WP4 case-status contract beyond review accept/reject.";

  async function onReVerify(finding: VerificationFindingDto) {
    setPending(ACTION.reVerify);
    setNotice(null);
    try {
      const created = await verificationSingleFlightMutation(
        verificationCanonicalClients.runsCreate.operation,
        {
          query: { env: environment },
          body: {
            actorId: me,
            label: `Re-verify ${finding.ref}`,
            controlIds: [finding.controlId],
          },
        },
      );
      setNotice(`Queued ${created.run.label}.`);
      onNavigate(href("runs", created.run.id));
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (!envReady) {
    return (
      <PageFrame page="inbox" title="Reviews">
        <PagePending title="Choose an environment">
          The active Environment is required before the inbox can load.
        </PagePending>
      </PageFrame>
    );
  }

  if (queueApi.isLoading && !queueApi.data) {
    return (
      <PageFrame page="inbox" title="Reviews">
        <PagePending title="Loading inbox">Fetching work waiting on a human decision.</PagePending>
      </PageFrame>
    );
  }

  if (queueApi.error && !queueApi.data) {
    return (
      <PageFrame page="inbox" title="Reviews">
        <PageError
          title="Inbox is unavailable"
          error={queueApi.error}
          onRetry={() => void queueApi.mutate()}
        />
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="inbox"
      title="Reviews"
      actions={
        <div className="vp-topbar-action-cluster">
          <ListSearch
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search inbox"
            aria-label="Search inbox"
          />
          <ScopeTabs
            value={scope}
            onChange={setScope}
            options={[
              { key: "mine", label: `Mine (${mine.length})`, actionId: ACTION.scopeMine },
              { key: "all", label: `Open (${all.length})`, actionId: ACTION.scopeAll },
              { key: "unread", label: `Unread (${unread.length})`, actionId: ACTION.scopeUnread },
              { key: "done", label: `Done (${done.length})`, actionId: ACTION.scopeDone },
            ]}
          />
          <Select
            value={verdict}
            onValueChange={(value) => setVerdict(value as QueueVerdictFilter)}
          >
            <SelectTrigger
              className="vp-inbox-select"
              data-action-id={ACTION.filterVerdict}
              aria-label="Filter inbox by verdict"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All verdicts</SelectItem>
              <SelectItem value="FAIL">Fail</SelectItem>
              <SelectItem value="UNKNOWN">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={severity}
            onValueChange={(value) => setSeverity(value as QueueSeverityFilter)}
          >
            <SelectTrigger
              className="vp-inbox-select"
              data-action-id={ACTION.filterSeverity}
              aria-label="Filter inbox by severity"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severity</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(value) => setSort(value as QueueSort)}>
            <SelectTrigger
              className="vp-inbox-select"
              data-action-id={ACTION.sort}
              aria-label="Sort inbox"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="severity">Severity</SelectItem>
              <SelectItem value="updated">Recently updated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      }
    >
      {notice ? (
        <ReadOnlyFailureCallout
          notice={notice}
          title="Action result"
          actionId="queue.action-status"
        />
      ) : null}

      {environment === "demo" ? (
        <Callout tone="neutral" title="Demo finding reviews">
          Contract-bound review requests are isolated to Real. Demo continues to show its scoped
          finding queue below.
        </Callout>
      ) : universalApi.error ? (
        <Callout tone="unknown" title="Contract-bound reviews unavailable">
          The canonical review ledger could not be loaded. Finding reviews remain visible below as a
          separate operational queue.
        </Callout>
      ) : universalApi.isLoading && !universalApi.data ? (
        <PagePending title="Loading contract-bound reviews">
          Reading review authority, receipt bindings, named human checks, and resolutions.
        </PagePending>
      ) : universalApi.data && completionProjection ? (
        <>
          {completionProjection.incomplete.reviews.length > 0 ? (
            <Callout tone="unknown" title="Historical review records are incomplete">
              {completionProjection.incomplete.reviews.length} readable review
              {completionProjection.incomplete.reviews.length === 1 ? " is" : "s are"} not bound to
              a current Work item and signed receipt. Recorded outcomes remain inspectable below but
              cannot authorize an action.
            </Callout>
          ) : null}
          <Callout tone="neutral" title="Human oracle authority is receipt-bound">
            Human determinations in this queue are first-class review records tied to signed
            receipts. They are not comments, transcript claims, or worker attestations; only the
            assigned independent authority can resolve them, and appeals create new review records
            instead of rewriting the original receipt.
          </Callout>
          <Section
            title="Contract-bound reviews"
            note={`${completionProjection.current.reviews.filter((review) => review.resolution === null).length} open of ${completionProjection.current.reviews.length} current · canonical ledger generated ${formatDateTime(universalApi.data.generatedAt)}.`}
          >
            <DataTable
              rows={universalApi.data.reviews}
              rowKey={(review) => review.request.id}
              emptyState={{
                title: "No contract-bound reviews",
                description:
                  "Human checks, appeals, and disputes appear here when a signed receipt requires independent review.",
              }}
              columns={[
                {
                  key: "review",
                  header: "Review",
                  render: (review) => (
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="font-medium text-foreground">{review.request.reason}</span>
                      <Mono>{review.request.id}</Mono>
                    </div>
                  ),
                },
                {
                  key: "kind",
                  header: "Kind",
                  render: (review) => <ToneBadge tone="neutral">{review.request.kind}</ToneBadge>,
                },
                {
                  key: "humanOracle",
                  header: "Human oracle",
                  render: (review) =>
                    review.request.humanCheck ? (
                      <div
                        className="flex min-w-0 flex-col gap-1"
                        data-human-oracle-review={review.request.id}
                        data-human-oracle-obligation={review.request.humanCheck.obligationId}
                        data-human-oracle-evidence={review.request.humanCheck.evidenceArtifactIds.join(
                          ",",
                        )}
                      >
                        <Mono>{review.request.humanCheck.obligationId}</Mono>
                        <span className="type-subtext text-muted-foreground">
                          {review.request.humanCheck.instruction}
                        </span>
                        <span className="type-subtext text-muted-foreground">
                          Evidence grant: {review.request.humanCheck.evidenceArtifactIds.join(", ")}
                        </span>
                        <span className="type-subtext text-muted-foreground">
                          Reviewers may abstain when the granted evidence is insufficient.
                        </span>
                      </div>
                    ) : (
                      <span className="type-subtext text-muted-foreground">
                        Appeal/dispute — no named human check.
                      </span>
                    ),
                },
                {
                  key: "work",
                  header: "Work",
                  render: (review) =>
                    universalApi.data?.work.find((work) => work.id === review.request.workItemId)
                      ?.title ?? review.request.workItemId,
                },
                {
                  key: "authority",
                  header: "Authority",
                  render: (review) => (
                    <div
                      className="flex min-w-0 flex-col gap-1"
                      data-human-oracle-authority={review.request.assignedAuthorityActorId ?? ""}
                    >
                      <Mono>{review.request.assignedAuthorityActorId ?? "Unassigned"}</Mono>
                      <span className="type-subtext text-muted-foreground">
                        Named authority; worker self-review is rejected by the canonical resolver.
                      </span>
                    </div>
                  ),
                },
                {
                  key: "receipt",
                  header: "Receipt",
                  render: (review) => <Mono>{review.request.receiptId}</Mono>,
                },
                {
                  key: "state",
                  header: "State",
                  render: (review) => {
                    const current = currentReviewIds.has(review.request.id);
                    const recorded = review.resolution ? review.resolution.outcome : "OPEN";
                    return (
                      <div className="flex flex-col gap-1">
                        <ToneBadge tone={current && review.resolution ? "pass" : "unknown"}>
                          {current ? recorded : "INCOMPLETE"}
                        </ToneBadge>
                        {!current ? (
                          <span className="type-subtext text-muted-foreground">
                            Recorded {recorded}
                          </span>
                        ) : null}
                      </div>
                    );
                  },
                },
              ]}
            />
          </Section>
        </>
      ) : null}

      <div className="vp-inbox-layout" data-inbox-layout="">
        <aside className="vp-inbox-left" aria-label="Inbox list">
          <div className="vp-inbox-list-head">
            <div>
              <div className="vp-inbox-list-title">Finding reviews</div>
              <div className="vp-inbox-list-subtitle">
                {list.length} shown · {unread.length} unread · {all.length} open
              </div>
            </div>
            <ToneBadge tone={scope === "done" ? "pass" : "neutral"}>{scope}</ToneBadge>
          </div>
          <div className="vp-inbox-list" role="listbox" aria-label="Inbox items">
            {list.length === 0 ? (
              <div className="vp-inbox-empty">
                <strong>
                  {scoped.length === 0 ? "Nothing waiting on you" : "No findings match"}
                </strong>
                <span>
                  {scoped.length === 0
                    ? "Open findings land here priority-first. Try Open or browse all findings."
                    : "Clear search or broaden the filters to see more items."}
                </span>
                <ActionButton
                  actionId="inbox.browse-findings"
                  variant="outline"
                  onClick={() => onNavigate(href("findings"))}
                >
                  Browse findings
                </ActionButton>
              </div>
            ) : (
              list.map((finding) => {
                const unreadRow = isUnread(finding);
                const owner = finding.assignedActorId ?? finding.ownerActorId ?? "Unassigned";
                const selectedRow = selected?.id === finding.id;
                return (
                  <button
                    key={finding.id}
                    type="button"
                    className="vp-inbox-row"
                    data-action-id="inbox.select-item"
                    data-queue-item={finding.id}
                    data-selected={selectedRow ? "true" : undefined}
                    data-unread={unreadRow ? "true" : undefined}
                    data-action-required={finding.status === "open" ? "true" : undefined}
                    role="option"
                    aria-selected={selectedRow}
                    aria-label={`Select item: ${finding.title}`}
                    onClick={() => setSelectedId(finding.id)}
                  >
                    <span className="vp-inbox-row-unread" aria-hidden="true" />
                    <span className="vp-inbox-row-main">
                      <span className="vp-inbox-row-sender">{owner}</span>
                      <span className="vp-inbox-row-title">{finding.title}</span>
                      <span className="vp-inbox-row-preview">{finding.summary}</span>
                      <span className="vp-inbox-row-meta">
                        <Mono>{finding.ref}</Mono>
                        <span>{formatRelativeTime(finding.openedAt)}</span>
                        {finding.dueAt ? (
                          <span>Due {formatRelativeTime(finding.dueAt)}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className="vp-inbox-row-badges">
                      <Verdict status={finding.verificationStatus} />
                      <Severity level={finding.severity} />
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>
        <div className="vp-inbox-detail" aria-live="polite">
          {selected ? (
            (() => {
              const finding = selected;
              const open = finding.status === "open";
              const unreadSelected = isUnread(finding);
              const owner = finding.assignedActorId ?? finding.ownerActorId ?? "Unassigned";
              const explicitUnknownReason =
                finding.verificationStatus === "UNKNOWN"
                  ? "UNKNOWN means the evidence cannot decide the question either way. It cannot be accepted as passing silently; open the finding and record an explicit decision."
                  : QUEUE_RATIONALE_REASON;
              const reviewDisabled =
                !open || pending !== null
                  ? pending
                    ? "Another queue action is in flight."
                    : "This finding already has a recorded decision."
                  : finding.evidenceArtifactIds.length === 0
                    ? "Need evidence artifacts to record a review."
                    : explicitUnknownReason;
              return (
                <article className="vp-inbox-content" key={finding.id}>
                  <header className="vp-inbox-content-head">
                    <div className="vp-inbox-content-titleblock">
                      <div className="vp-inbox-content-kicker">
                        <Mono>{finding.ref}</Mono>
                        <span>{owner}</span>
                        {unreadSelected ? (
                          <ToneBadge tone="accent">Unread</ToneBadge>
                        ) : (
                          <ToneBadge tone="neutral">Read</ToneBadge>
                        )}
                      </div>
                      <h2>{finding.title}</h2>
                      <p>{finding.summary}</p>
                    </div>
                    <PageActions
                      menu={
                        <ActionMenu
                          actionId={ACTION.more}
                          items={[
                            {
                              label: "Reassign",
                              actionId: ACTION.reassign,
                              disabled: true,
                              reason: reassignReason,
                            },
                            {
                              label: "Escalate",
                              actionId: ACTION.escalate,
                              disabled: true,
                              reason: escalateReason,
                            },
                            {
                              label: "Re-verify",
                              actionId: ACTION.reVerify,
                              disabled: pending !== null,
                              reason:
                                pending !== null ? "Another queue action is in flight." : undefined,
                              onClick: () => void onReVerify(finding),
                            },
                          ]}
                        />
                      }
                      secondary={
                        <>
                          <ActionButton
                            actionId={unreadSelected ? ACTION.markRead : ACTION.markUnread}
                            variant="outline"
                            onClick={() =>
                              unreadSelected ? markRead(finding.id) : markUnread(finding.id)
                            }
                          >
                            {unreadSelected ? "Mark read" : "Mark unread"}
                          </ActionButton>
                          <ActionButton
                            actionId={ACTION.accept}
                            variant="accept"
                            disabled
                            reason={reviewDisabled}
                          >
                            Accept
                          </ActionButton>
                          <ActionButton
                            actionId={ACTION.reject}
                            variant="reject"
                            disabled
                            reason={reviewDisabled}
                          >
                            Reject
                          </ActionButton>
                        </>
                      }
                      primary={
                        <ActionButton
                          actionId={ACTION.open}
                          variant="primary"
                          onClick={() => onNavigate(href("findings", publicFindingParam(finding)))}
                        >
                          Open and decide
                        </ActionButton>
                      }
                    />
                  </header>

                  <section className="vp-inbox-decision-panel">
                    <div>
                      <div className="vp-inbox-section-label">Decision state</div>
                      <div className="vp-inbox-status-row">
                        <Verdict status={finding.verificationStatus} size="lg" />
                        <Severity level={finding.severity} />
                        <ToneBadge tone={open ? "unknown" : "pass"}>{finding.status}</ToneBadge>
                      </div>
                    </div>
                    <p>{reviewDisabled}</p>
                  </section>

                  <div className="vp-inbox-content-grid">
                    <section className="vp-inbox-section">
                      <div className="vp-inbox-section-label">Question</div>
                      <p>{finding.ask}</p>
                    </section>
                    <section className="vp-inbox-section">
                      <div className="vp-inbox-section-label">Evidence</div>
                      {finding.evidenceArtifactIds.length > 0 ? (
                        <div className="vp-inbox-token-list">
                          {finding.evidenceArtifactIds.map((id) => (
                            <Mono key={id}>{id}</Mono>
                          ))}
                        </div>
                      ) : (
                        <p>No evidence artifacts are attached yet.</p>
                      )}
                    </section>
                    <section className="vp-inbox-section">
                      <div className="vp-inbox-section-label">Workflow context</div>
                      <Fields>
                        <Field label="Control">
                          <Mono>{finding.controlId}</Mono>
                        </Field>
                        <Field label="Requirement">
                          <Mono>{finding.requirementId}</Mono>
                        </Field>
                        <Field label="Run">
                          <Mono>{finding.runId}</Mono>
                        </Field>
                        <Field label="Opened">{formatDateTime(finding.openedAt)}</Field>
                        <Field label="Due">
                          {finding.dueAt ? formatDateTime(finding.dueAt) : "No due date"}
                        </Field>
                      </Fields>
                    </section>
                    <section className="vp-inbox-section">
                      <div className="vp-inbox-section-label">Citations</div>
                      {finding.citationRefs.length > 0 ? (
                        <div className="vp-inbox-token-list">
                          {finding.citationRefs.map((ref) => (
                            <Mono key={ref}>{ref}</Mono>
                          ))}
                        </div>
                      ) : (
                        <p>No citations have been recorded.</p>
                      )}
                    </section>
                  </div>
                </article>
              );
            })()
          ) : (
            <div className="vp-inbox-empty vp-inbox-empty-detail">
              <strong>Select an inbox item</strong>
              <span>
                Choose a finding from the left pane to review evidence, context, and decision
                actions.
              </span>
            </div>
          )}
        </div>
      </div>
    </PageFrame>
  );
}

export function FindingsPage({ onNavigate }: VerificationPageProps) {
  return <FindingsBody onNavigate={onNavigate} />;
}

function FindingsBody({ onNavigate }: VerificationPageProps) {
  const envCtx = useVerificationEnvOptional();
  const environment: VerificationEnvironment = envCtx?.environment ?? "demo";
  const envReady = envCtx ? envCtx.ready && envCtx.environment !== null : true;

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"open" | "all" | "closed">("open");
  const [severity, setSeverity] = useState<"all" | "critical" | "high" | "medium" | "low">("all");

  const findingsApi = useApi(
    verificationCanonicalClients.findingsList.operation,
    envReady ? { query: { env: environment } } : null,
  );

  const items = useMemo(() => {
    if (!envReady) return [];
    return findingsApi.data?.items ?? [];
  }, [envReady, findingsApi.data]);

  const rows = useMemo(() => {
    let list = items;
    if (status === "open") list = list.filter((f) => f.status === "open");
    if (status === "closed") list = list.filter((f) => f.status !== "open");
    if (severity !== "all") list = list.filter((f) => f.severity === severity);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(
        (f) =>
          f.title.toLowerCase().includes(needle) ||
          f.ref.toLowerCase().includes(needle) ||
          f.summary.toLowerCase().includes(needle),
      );
    }
    return worstFirst(list);
  }, [items, q, severity, status]);

  const clusters = useMemo(
    () =>
      clusterFindings(
        rows.map((f) => ({
          id: f.id,
          tenantId: "tenant_verification",
          controlId: f.controlId,
          connectorId: null,
          connectorScopeSatisfied: true,
          collectedAt: f.openedAt,
          policyRequirementId: f.requirementId,
          ownerActorId: f.ownerActorId,
        })),
        { now: new Date().toISOString(), stalenessThresholdHours: 24 * 30 },
      ),
    [rows],
  );
  const openCount = items.filter((finding) => finding.status === "open").length;
  const closedCount = items.filter((finding) => finding.status !== "open").length;

  if (!envReady) {
    return (
      <PageFrame page="findings" wide title="Findings">
        <PagePending title="Choose an environment">
          The active Environment is required before findings can load.
        </PagePending>
      </PageFrame>
    );
  }

  if (findingsApi.isLoading && !findingsApi.data) {
    return (
      <PageFrame page="findings" wide title="Findings">
        <PagePending title="Loading findings">
          Fetching FAIL and UNKNOWN across this workspace.
        </PagePending>
      </PageFrame>
    );
  }

  if (findingsApi.error && !findingsApi.data) {
    return (
      <PageFrame page="findings" wide title="Findings">
        <PageError
          title="Findings are unavailable"
          error={findingsApi.error}
          onRetry={() => void findingsApi.mutate()}
        />
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="findings"
      wide
      title="Findings"
      lede="Every FAIL and UNKNOWN across the tenant. Use Inbox filters for one-at-a-time decision work."
      actions={
        <ScopeTabs
          value={status}
          onChange={setStatus}
          options={[
            { key: "open", label: `Open (${openCount})`, actionId: FINDINGS_ACTION.filterStatus },
            {
              key: "all",
              label: `All (${items.length})`,
              actionId: FINDINGS_ACTION.filterStatusAll,
            },
            {
              key: "closed",
              label: `Closed (${closedCount})`,
              actionId: FINDINGS_ACTION.filterStatusClosed,
            },
          ]}
        />
      }
    >
      {clusters.length > 0 ? (
        <Callout
          tone="neutral"
          actionId="findings.clusters"
          title={clusterCopy(clusters, rows).title}
        >
          {clusterCopy(clusters, rows).body}
        </Callout>
      ) : null}

      <DataTable
        toolbar={
          <FilterToolbar>
            <FilterField label="Search" wide>
              <ListSearch
                data-action-id={FINDINGS_ACTION.search}
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Search findings, refs, summaries"
                aria-label="Search findings"
              />
            </FilterField>
            <FilterField label="Severity">
              <Select
                value={severity}
                onValueChange={(value) =>
                  setSeverity(value as "all" | "critical" | "high" | "medium" | "low")
                }
              >
                <SelectTrigger
                  className="w-full"
                  data-action-id={FINDINGS_ACTION.filterSeverity}
                  aria-label="Filter by severity"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
          </FilterToolbar>
        }
        rows={rows}
        rowKey={(finding) => finding.id}
        rowId={(finding) => `finding-row-${finding.id}`}
        rowActionId={FINDINGS_ACTION.openRow}
        onRowClick={(finding) => onNavigate(href("findings", publicFindingParam(finding)))}
        emptyState={{
          title: "No open findings",
          description: (
            <>
              Failures and unknowns will list here once a run has produced them.{" "}
              <PageLink onNavigate={onNavigate} to="inbox">
                Open the inbox
              </PageLink>
              .
            </>
          ),
        }}
        columns={FINDINGS_COLUMNS}
      />
    </PageFrame>
  );
}

export function FindingDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  return <FindingDetailBody id={id} onNavigate={onNavigate} />;
}

function FindingDetailBody({ id, onNavigate }: VerificationDetailPageProps) {
  const envCtx = useVerificationEnvOptional();
  const environment: VerificationEnvironment = envCtx?.environment ?? "demo";
  const envReady = envCtx ? envCtx.ready && envCtx.environment !== null : true;
  const me = environmentActor(environment);

  const [rationale, setRationale] = useState("");
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const findingsApi = useApi(
    verificationCanonicalClients.findingsList.operation,
    envReady ? { query: { env: environment } } : null,
  );

  const finding = useMemo(() => {
    if (!envReady) return null;
    return (findingsApi.data?.items ?? []).find((row) => findingMatchesParam(row, id)) ?? null;
  }, [envReady, findingsApi.data, id]);

  // The recorded decisions for this finding, read back from the server rather
  // than echoed from the POST: the property under test is that the rationale
  // SURVIVED, and a client-side echo would pass identically if it had not.
  const decisionsApi = useApi(
    verificationCanonicalClients.findingsDecisions.operation,
    envReady && finding ? { params: { id: finding.id }, query: { env: environment } } : null,
  );
  const decisions = decisionsApi.data?.items ?? [];
  const draftScorecard = decisionsApi.data?.draftScorecard ?? null;

  async function onDecide(decision: "accept_exception" | "reject_remediation") {
    if (!finding) return;
    const trimmed = rationale.trim();
    if (!trimmed) {
      setNotice("Write a rationale before recording a decision. Silent approve is a hard zero.");
      return;
    }
    const draft = draftRationale({
      finding: { id: finding.id },
      evidence: finding.evidenceArtifactIds.map((ref) => ({ ref, observedAt: finding.openedAt })),
      // Null, not a worker name. Nothing in this product drafts a rationale
      // today — the reviewer typed every character of `trimmed` into the box
      // above. Naming a drafter here (this line read `"queue-assistant"`, a
      // string that appeared nowhere else in the repo) made the reviewer's own
      // words look like a draft signed with zero edits, which is precisely the
      // reading `draftedRationaleEditRate` raises as rubber-stamping. When a
      // drafting worker really lands, it passes its own id here and the
      // scorecard starts measuring something real.
      worker: null,
      text: trimmed,
      now: new Date().toISOString(),
    });
    const signed = signRationale({
      draft,
      editedText: trimmed,
      actorId: me,
      now: new Date().toISOString(),
    });
    if (!signed.ok) {
      setNotice(`Rationale refused: ${signed.reason}`);
      return;
    }
    if (finding.evidenceArtifactIds.length === 0) {
      setNotice("Need evidence artifacts to record a review.");
      return;
    }
    setPending(decision === "accept_exception" ? FINDINGS_ACTION.accept : FINDINGS_ACTION.reject);
    setNotice(null);
    try {
      await verificationSingleFlightMutation(
        verificationCanonicalClients.findingsReview.operation,
        {
          params: { id: finding.id },
          query: { env: environment },
          body: {
            actorId: me,
            decision,
            rationale: signed.decision.finalText,
            evidenceArtifactIds: finding.evidenceArtifactIds,
            policyId: finding.controlId,
            draftedByWorker: signed.decision.draftedByWorker,
            draftEditDistance: signed.decision.editDistance,
          },
        },
      );
      setRationale("");
      setNotice(`Recorded ${decision} on ${finding.ref}.`);
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (!envReady) {
    return (
      <PageFrame page="finding" wide title="Finding">
        <PagePending title="Choose an environment">
          The active Environment is required before finding detail can load.
        </PagePending>
      </PageFrame>
    );
  }

  if (findingsApi.isLoading && !findingsApi.data) {
    return (
      <PageFrame page="finding" wide title="Finding">
        <PagePending title="Loading finding">
          Fetching the decision surface for this finding.
        </PagePending>
      </PageFrame>
    );
  }

  if (!finding) {
    return (
      <PageFrame
        page="finding"
        wide
        title="Finding"
        actions={
          <ActionButton actionId="findings.open-search" onClick={() => openVerificationSearch()}>
            Open search
          </ActionButton>
        }
      >
        <PagePending title={`No finding named ${id}`}>
          That id is not in this environment. Search for the displayed finding number (for example
          F-4401), or{" "}
          <PageLink onNavigate={onNavigate} to="findings">
            back to findings
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  const rationaleMissing = rationale.trim().length === 0;
  const reviewDisabled =
    finding.status !== "open"
      ? "This finding already has a recorded decision."
      : pending
        ? "Another findings action is in flight."
        : finding.evidenceArtifactIds.length === 0
          ? "Need evidence artifacts to record a review."
          : rationaleMissing
            ? "Write a rationale before recording a decision."
            : undefined;
  const evidenceRows = finding.evidenceArtifactIds.map((artifactId) => ({
    id: artifactId,
    label: stripEnvironmentPrefix(artifactId),
  }));

  return (
    <PageFrame
      page="finding"
      wide
      title={finding.title}
      lede="The decision surface: what failed or could not be decided, the evidence it cited, and the written rationale a receipt will bind."
    >
      {notice ? (
        <ReadOnlyFailureCallout
          notice={notice}
          title="Action result"
          actionId="findings.action-status"
        />
      ) : null}

      <FindingCard finding={finding} hideTitle />

      <Card>
        <CardContent>
          <Fields>
            <Field label="Finding">
              <Mono>{finding.ref}</Mono>
            </Field>
            <Field label="Control">
              <Mono>{stripEnvironmentPrefix(finding.controlId)}</Mono>
            </Field>
            <Field label="Policy">
              <Mono>{stripEnvironmentPrefix(finding.requirementId)}</Mono>
            </Field>
            {finding.citationRefs.length > 0 ? (
              <Field label="Observations">
                {finding.citationRefs.map((ref) => observationLabel(ref, evidenceRows)).join(" · ")}
              </Field>
            ) : null}
          </Fields>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`rationale-${finding.id}`}>Rationale</Label>
            <Textarea
              id={`rationale-${finding.id}`}
              data-action-id={FINDINGS_ACTION.sign}
              value={rationale}
              onChange={(event) => setRationale(event.target.value)}
              rows={4}
              placeholder="Why this is an exception, or why it must be remediated."
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ActionButton
              actionId={FINDINGS_ACTION.accept}
              variant="accept"
              disabled={Boolean(reviewDisabled)}
              reason={reviewDisabled}
              onClick={() => void onDecide("accept_exception")}
            >
              Accept
            </ActionButton>
            <ActionButton
              actionId={FINDINGS_ACTION.reject}
              variant="reject"
              disabled={Boolean(reviewDisabled)}
              reason={reviewDisabled}
              onClick={() => void onDecide("reject_remediation")}
            >
              Reject
            </ActionButton>
          </div>
        </CardContent>
      </Card>

      <Section
        title="Recorded decisions"
        note={
          draftScorecard && draftScorecard.drafted > 0
            ? `${decisions.length} decision(s) on this finding. Across this tenant, ${draftScorecard.edited} of ${draftScorecard.drafted} worker-drafted rationales were edited before signing (${Math.round(draftScorecard.rate * 100)}%).`
            : `${decisions.length} decision(s) on this finding. No worker-drafted rationales in this tenant yet, so there is no edit rate to report.`
        }
      >
        {decisions.length === 0 ? (
          <Card>
            <CardContent>
              <span className="type-body text-muted-foreground">
                No decision has been recorded on this finding yet. When one is, the reviewer&apos;s
                written reason is stored verbatim and shown here.
              </span>
            </CardContent>
          </Card>
        ) : (
          <Stack>
            {decisions.map((record) => (
              <Card key={record.id}>
                <CardContent className="flex flex-col gap-3">
                  <Fields>
                    <Field label="Decision">
                      <Mono>{record.decision}</Mono>
                    </Field>
                    <Field label="Decided by">
                      <Mono>{stripEnvironmentPrefix(record.actorId)}</Mono>
                    </Field>
                    <Field label="Decided at">{formatDateTime(record.decidedAt)}</Field>
                    <Field label="Rationale drafted by">
                      {record.draftedByWorker
                        ? `${stripEnvironmentPrefix(record.draftedByWorker)} · ${record.draftEditDistance ?? 0} edit(s) before signing`
                        : "the reviewer (not drafted)"}
                    </Field>
                  </Fields>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`recorded-rationale-${record.id}`}>Rationale (verbatim)</Label>
                    <p
                      id={`recorded-rationale-${record.id}`}
                      data-action-id={FINDINGS_ACTION.recordedRationale}
                      data-action-target={record.id}
                      className="type-body whitespace-pre-wrap text-foreground"
                    >
                      {record.rationale}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Section>

      {evidenceRows.length > 0 ? (
        <Stack>
          {evidenceRows.map((artifact) => (
            <Card key={artifact.id} interactive asChild>
              <button
                type="button"
                className="w-full text-left"
                data-action-id={FINDINGS_ACTION.openEvidence}
                data-action-target={artifact.id}
                onClick={() => onNavigate(href("evidence", artifact.id))}
              >
                <CardContent className="flex flex-col gap-1">
                  <span className="type-body font-medium">Evidence</span>
                  <span className="type-body text-foreground">{artifact.label}</span>
                </CardContent>
              </button>
            </Card>
          ))}
        </Stack>
      ) : null}

      <p>
        <PageLink onNavigate={onNavigate} to="findings">
          Back to findings
        </PageLink>
      </p>
    </PageFrame>
  );
}

export function RunsPage({ onNavigate }: VerificationPageProps) {
  return <RunsBody onNavigate={onNavigate} />;
}

type RunRow = {
  id: string;
  label: string;
  lifecycle: VerificationRunDto["lifecycle"];
  aggregate: string;
  controlIds: string[];
  startedAt: string | null;
  completedAt: string | null;
  createdByActorId: string;
  receiptId: string | null;
  engineVersion: string;
  evidenceArtifactIds: string[];
  demoScoped?: boolean;
};

function dtoToRow(run: VerificationRunDto, environment: VerificationEnvironment): RunRow {
  return {
    id: run.id,
    label: run.label,
    lifecycle: run.lifecycle,
    aggregate: run.aggregate,
    controlIds: [...run.controlIds],
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    createdByActorId: run.createdByActorId,
    receiptId: run.receiptId,
    engineVersion: run.engineVersion,
    evidenceArtifactIds: [...run.evidenceArtifactIds],
    demoScoped: environment === "demo",
  };
}

function RunVerdict({ status }: { status: string }) {
  return <Verdict status={status} />;
}

const RUNS_LEDE =
  "Worker attempts and independent verification runs, kept separate so the worker that produced evidence is never mistaken for the verifier that issued the verdict.";

function RunsBody({ onNavigate }: VerificationPageProps) {
  const envCtx = useVerificationEnvOptional();
  const environment: VerificationEnvironment = envCtx?.environment ?? "demo";
  const envReady = envCtx ? envCtx.ready && envCtx.environment !== null : true;
  const me = environmentActor(environment);

  const [q, setQ] = useState("");
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const runsApi = useApi(
    verificationCanonicalClients.runsList.operation,
    envReady ? { query: { env: environment } } : null,
  );
  const universalApi = useApi(
    universalVerification.overview,
    envReady && environment === "real" ? {} : null,
  );
  const completionProjection = useMemo(
    () => (universalApi.data ? projectUniversalCompletionOverview(universalApi.data) : null),
    [universalApi.data],
  );
  const readableAttempts = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.attempts,
            completionProjection.incomplete.attempts,
            (attempt) => attempt.binding.attemptId,
          )
        : [],
    [completionProjection],
  );
  const readableWork = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.work,
            completionProjection.incomplete.work,
            (work) => work.id,
          )
        : [],
    [completionProjection],
  );
  const readableContracts = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.contracts,
            completionProjection.incomplete.contracts,
            (contract) => contract.contractDigest,
          )
        : [],
    [completionProjection],
  );
  const currentAttemptIds = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.attempts ?? [],
        (attempt) => attempt.binding.attemptId,
      ),
    [completionProjection],
  );

  const items = useMemo(() => {
    if (!envReady) return [];
    return (runsApi.data?.items ?? []).map((run) => dtoToRow(run, environment));
  }, [envReady, environment, runsApi.data]);

  const rows = useMemo(() => {
    let list = items;
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(
        (run) => run.label.toLowerCase().includes(needle) || run.id.toLowerCase().includes(needle),
      );
    }
    return [...list].sort((a, b) => Date.parse(b.startedAt ?? "") - Date.parse(a.startedAt ?? ""));
  }, [items, q]);

  async function onReplay(run: RunRow) {
    if (pending) return;
    setPending(run.id);
    setNotice(null);
    try {
      if (run.controlIds.length === 0) {
        setNotice("Need control ids to replay this run.");
        return;
      }
      const created = await verificationSingleFlightMutation(
        verificationCanonicalClients.runsCreate.operation,
        {
          query: { env: environment },
          body: {
            actorId: me,
            label: `Replay ${run.label}`,
            controlIds: run.controlIds,
            engineVersion: run.engineVersion,
          },
        },
      );
      setNotice(`Queued ${created.run.label}.`);
      onNavigate(href("runs", created.run.id));
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (!envReady) {
    return (
      <PageFrame page="runs" wide title="Runs" lede={RUNS_LEDE}>
        <PagePending title="Choose an environment">
          The active Environment is required before runs can load.
        </PagePending>
      </PageFrame>
    );
  }

  if (runsApi.isLoading && !runsApi.data) {
    return (
      <PageFrame page="runs" wide title="Runs" lede={RUNS_LEDE}>
        <PagePending title="Loading runs">
          Fetching the WP4a runs contract for this workspace.
        </PagePending>
      </PageFrame>
    );
  }

  if (runsApi.error && !runsApi.data) {
    return (
      <PageFrame page="runs" wide title="Runs" lede={RUNS_LEDE}>
        <PageError
          title="Runs are unavailable"
          error={runsApi.error}
          onRetry={() => void runsApi.mutate()}
        />
        <ButtonRow>
          <ActionButton actionId={RUNS_REPLAY_ACTION} data-action-id={RUNS_REPLAY_ACTION} disabled>
            Replay run
          </ActionButton>
          <ActionButton
            actionId={RUNS_OPEN_RECEIPT_ACTION}
            data-action-id={RUNS_OPEN_RECEIPT_ACTION}
            variant="primary"
            disabled
          >
            Open receipt
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }

  return (
    <PageFrame page="runs" wide title="Runs" lede={RUNS_LEDE}>
      {notice ? (
        <ReadOnlyFailureCallout
          notice={notice}
          title="Action result"
          actionId="runs.action-status"
        />
      ) : null}

      {environment === "demo" ? (
        <Callout tone="neutral" title="Demo verification runs">
          Canonical worker attempts are isolated to Real. Demo continues to show its scoped
          verification runs below.
        </Callout>
      ) : universalApi.error ? (
        <Callout tone="unknown" title="Worker attempts unavailable">
          The canonical attempt ledger could not be loaded. Verification runs below are not
          presented as worker execution records.
        </Callout>
      ) : universalApi.isLoading && !universalApi.data ? (
        <PagePending title="Loading worker attempts">
          Reading attempt identity, contract binding, adapter mode, and submission state.
        </PagePending>
      ) : universalApi.data ? (
        <Section
          title="Worker attempts"
          note={`Canonical Work ledger generated ${formatDateTime(universalApi.data.generatedAt)}. Only attempts bound to current completion-aware Work and contracts can participate in a verdict.`}
        >
          {(completionProjection?.incomplete.attempts.length ?? 0) > 0 ? (
            <Callout tone="unknown" title="Historical attempts are incomplete">
              Their recorded identity, adapter, and lifecycle state remain inspectable, but they
              cannot authorize enforcement or count as a completed run.
            </Callout>
          ) : null}
          <DataTable
            rows={readableAttempts}
            rowKey={(attempt) => attempt.binding.attemptId}
            rowActionId="runs.open-worker-attempt"
            onRowClick={(attempt) => onNavigate(href("runs", attempt.binding.attemptId))}
            emptyState={{
              title: "No worker attempts",
              description:
                "A run appears here only after an accepted contract is bound to a worker identity and input snapshot.",
            }}
            columns={[
              {
                key: "work",
                header: "Work",
                cellClassName: "whitespace-normal",
                render: (attempt) => {
                  const work = readableWork.find((item) => item.id === attempt.binding.workItemId);
                  return (
                    // Plain-language first: the row names what was attempted. The attempt,
                    // work, and contract ids stay on the row, one disclosure away.
                    <div
                      className="flex min-w-0 flex-col gap-1"
                      data-attempt-id={attempt.binding.attemptId}
                    >
                      <span className="font-medium text-foreground" data-attempt-plain>
                        {work?.title ?? "Work item outside the current ledger"}
                      </span>
                      <details onClick={(event) => event.stopPropagation()}>
                        <summary className="cursor-pointer type-subtext text-muted-foreground">
                          Reference IDs
                        </summary>
                        <div
                          className="mt-1 flex min-w-0 flex-col gap-0.5 break-all"
                          data-attempt-ids
                        >
                          <Mono>Attempt {attempt.binding.attemptId}</Mono>
                          <Mono>Work {attempt.binding.workItemId}</Mono>
                          <Mono>Contract {attempt.binding.contractId}</Mono>
                        </div>
                      </details>
                    </div>
                  );
                },
              },
              {
                key: "worker",
                header: "Worker",
                render: (attempt) => <Mono>{attempt.worker.actorId}</Mono>,
              },
              {
                key: "state",
                header: "State",
                render: (attempt) =>
                  currentAttemptIds.has(attempt.binding.attemptId) ? (
                    <ToneBadge
                      tone={
                        attempt.state === "SUBMITTED"
                          ? "pass"
                          : attempt.state === "ABANDONED"
                            ? "fail"
                            : "neutral"
                      }
                    >
                      {attempt.state}
                    </ToneBadge>
                  ) : (
                    <div className="flex flex-col gap-1" data-completion-state="incomplete">
                      <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
                      <span className="type-subtext text-muted-foreground">
                        Recorded {attempt.state}
                      </span>
                    </div>
                  ),
              },
              {
                key: "contract",
                header: "Contract",
                render: (attempt) => {
                  // The accepted contract has a title; show it instead of its id, and fall
                  // back to the id only when this ledger cannot name the contract.
                  const contract = readableContracts.find(
                    (row) =>
                      row.content.contractId === attempt.binding.contractId &&
                      row.contractDigest === attempt.binding.contractDigest,
                  );
                  return (
                    <div className="flex min-w-0 flex-col gap-0.5">
                      {contract ? (
                        <span>{contract.content.title}</span>
                      ) : (
                        <Mono>{attempt.binding.contractId}</Mono>
                      )}
                      <span className="type-subtext text-muted-foreground">
                        v{attempt.binding.contractVersion}
                      </span>
                    </div>
                  );
                },
              },
              {
                key: "adapter",
                header: "Adapter",
                render: (attempt) => <ToneBadge tone="neutral">{attempt.adapter.mode}</ToneBadge>,
              },
              {
                key: "opened",
                header: "Opened",
                render: (attempt) => formatRelativeTime(attempt.openedAt),
              },
            ]}
          />
        </Section>
      ) : null}

      <Section
        title="Verification runs"
        note="Independent evaluator executions from the existing control projection."
      >
        <DataTable
          toolbar={
            <FilterToolbar>
              <FilterField label="Search" wide>
                <ListSearch
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder="Search runs…"
                  aria-label="Search runs"
                />
              </FilterField>
            </FilterToolbar>
          }
          emptyState={{
            title: "No runs yet",
            description: (
              <>
                Run history appears here after the engine evaluates policies over evidence.{" "}
                <PageLink onNavigate={onNavigate} to="inbox">
                  Open the inbox
                </PageLink>
                .
              </>
            ),
          }}
          rows={rows}
          rowKey={(run) => run.id}
          onRowClick={(run) => onNavigate(href("runs", run.id))}
          columns={[
            {
              key: "run",
              header: "Run",
              cellClassName: "whitespace-normal",
              render: (run) => (
                <div
                  className="flex flex-col gap-0.5"
                  data-run-id={run.id}
                  data-demo-scoped={run.demoScoped ? "true" : undefined}
                >
                  <span className="font-medium text-foreground">{run.label}</span>
                  <Mono>
                    {run.id} · {run.engineVersion}
                  </Mono>
                </div>
              ),
            },
            {
              key: "verdict",
              header: "Verdict",
              render: (run) => <RunVerdict status={run.aggregate} />,
            },
            {
              key: "status",
              header: "Status",
              render: (run) => (
                <div className="flex flex-col gap-0.5">
                  <Mono>{run.lifecycle}</Mono>
                </div>
              ),
            },
            {
              key: "receipt",
              header: "Receipt",
              render: (run) =>
                run.receiptId ? (
                  <Mono>{run.receiptId}</Mono>
                ) : (
                  <ToneBadge tone="unknown">none</ToneBadge>
                ),
            },
            {
              key: "started",
              header: "Started",
              render: (run) => (
                <Mono>{run.startedAt ? formatRelativeTime(run.startedAt) : "—"}</Mono>
              ),
            },
            {
              key: "actions",
              header: "Actions",
              render: (run) => {
                const replayDisabled =
                  pending !== null
                    ? "Another run action is in flight."
                    : run.lifecycle !== "completed"
                      ? "Replay needs a completed run."
                      : run.controlIds.length === 0
                        ? "Need control ids to replay."
                        : undefined;
                const receiptDisabled = run.receiptId ? undefined : "This run issued no receipt.";
                return (
                  <ButtonRow onClick={(event) => event.stopPropagation()}>
                    <ActionButton
                      actionId={RUNS_REPLAY_ACTION}
                      data-action-id={RUNS_REPLAY_ACTION}
                      disabled={Boolean(replayDisabled)}
                      reason={replayDisabled}
                      onClick={() => void onReplay(run)}
                    >
                      Replay
                    </ActionButton>
                    <ActionButton
                      actionId={RUNS_OPEN_RECEIPT_ACTION}
                      data-action-id={RUNS_OPEN_RECEIPT_ACTION}
                      variant="primary"
                      disabled={Boolean(receiptDisabled)}
                      reason={receiptDisabled}
                      onClick={() => {
                        if (run.receiptId) onNavigate(href("receipts", run.receiptId));
                      }}
                    >
                      Open receipt
                    </ActionButton>
                  </ButtonRow>
                );
              },
            },
          ]}
        />
      </Section>
    </PageFrame>
  );
}

export function RunDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  return <RunDetailBody id={id} onNavigate={onNavigate} />;
}

type UniversalRunTab = "summary" | "contract" | "evidence" | "checks" | "receipt" | "effects";

function initialUniversalRunTab(): UniversalRunTab {
  if (typeof window === "undefined") return "summary";
  const tab = new URLSearchParams(window.location.search).get("tab");
  return tab === "contract" ||
    tab === "evidence" ||
    tab === "checks" ||
    tab === "receipt" ||
    tab === "effects"
    ? tab
    : "summary";
}

function UniversalRunDetail({
  attempt,
  overview,
  onNavigate,
  now = new Date().toISOString(),
}: {
  attempt: UniversalConsoleOverview["attempts"][number];
  overview: UniversalConsoleOverview;
  onNavigate: (to: string) => void;
  /**
   * THE RENDER EDGE for this run's verdict. One clock read, used by every place below
   * that shows what the receipt says — so the badge in the header and the badge on the
   * Receipt tab can never disagree about whether a deadline has passed.
   */
  now?: string;
}) {
  const [tab, setTab] = useState<UniversalRunTab>(initialUniversalRunTab);
  const completionProjection = projectUniversalCompletionOverview(overview);
  const readableWork = combineCompletionHistory(
    completionProjection.current.work,
    completionProjection.incomplete.work,
    (work) => work.id,
  );
  const readableContracts = combineCompletionHistory(
    completionProjection.current.contracts,
    completionProjection.incomplete.contracts,
    (contract) => contract.contractDigest,
  );
  const readableReceipts = combineCompletionHistory(
    completionProjection.current.receipts,
    completionProjection.incomplete.receipts,
    (receipt) => receipt.payload.id,
  );
  const readableReviews = combineCompletionHistory(
    completionProjection.current.reviews,
    completionProjection.incomplete.reviews,
    (review) => review.request.id,
  );
  const attemptIsCurrent = completionProjection.current.attempts.some(
    (item) => item.binding.attemptId === attempt.binding.attemptId,
  );
  const work = readableWork.find((item) => item.id === attempt.binding.workItemId) ?? null;
  const contract =
    readableContracts.find(
      (item) =>
        item.content.contractId === attempt.binding.contractId &&
        item.content.version === attempt.binding.contractVersion &&
        item.contractDigest === attempt.binding.contractDigest,
    ) ?? null;
  const receipt =
    readableReceipts.find((item) => item.payload.binding.attemptId === attempt.binding.attemptId) ??
    null;
  const contractIsCurrent = contract
    ? completionProjection.current.contracts.some(
        (item) => item.contractDigest === contract.contractDigest,
      )
    : false;
  const receiptIsStructurallyCurrent = receipt
    ? completionProjection.current.receipts.some(
        (item) => item.payloadDigest === receipt.payloadDigest,
      )
    : false;
  const receiptIsAuthoritative = Boolean(
    receipt && receiptIsStructurallyCurrent && receipt.payload.assurance === "PROSPECTIVE",
  );
  const receiptIsRetrospective = Boolean(receipt && isRetrospectiveHistoryReceipt(receipt));
  // READ TIME. The stored bytes are signed and cannot be settled in place, so a receipt
  // whose PENDING window closed reads here as what that window resolved to — through the
  // one law in `verdict-law.ts`, never by comparing dates in this file.
  const receiptVerdict = receipt ? resolvedVerdictOf(receipt, now) : null;
  const currentReviewIds = currentAuthorityIds(
    completionProjection.current.reviews,
    (review) => review.request.id,
  );
  const receiptDetail = useApi(
    universalVerification.receipt,
    receipt ? { params: { id: receipt.payload.id } } : null,
  );
  const reviews = readableReviews.filter(
    (review) =>
      review.request.workItemId === attempt.binding.workItemId &&
      review.request.binding.attemptId === attempt.binding.attemptId,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    tab === "summary" ? params.delete("tab") : params.set("tab", tab);
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(window.history.state, "", next);
    }
  }, [tab]);

  return (
    <PageFrame
      page="run"
      wide
      title={work?.title ?? attempt.binding.attemptId}
      lede={`Worker ${attempt.worker.actorId} opened this run ${formatDateTime(attempt.openedAt)} against ${attempt.binding.contractId} v${attempt.binding.contractVersion}.`}
      actions={
        <ActionButton actionId="runs.back" onClick={() => onNavigate(href("runs"))}>
          Back to runs
        </ActionButton>
      }
    >
      <ButtonRow>
        <ToneBadge
          tone={
            attemptIsCurrent
              ? attempt.state === "SUBMITTED"
                ? "pass"
                : attempt.state === "ABANDONED"
                  ? "fail"
                  : "neutral"
              : "unknown"
          }
        >
          {attemptIsCurrent ? attempt.state : `INCOMPLETE · recorded ${attempt.state}`}
        </ToneBadge>
        {receipt ? (
          receiptIsAuthoritative ? (
            <Verdict status={receiptVerdict ?? ""} />
          ) : receiptIsRetrospective ? (
            <ToneBadge tone="unknown">RETROSPECTIVE · recorded</ToneBadge>
          ) : (
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
          )
        ) : (
          <ToneBadge tone="unknown">No verdict</ToneBadge>
        )}
        <Mono>{attempt.binding.attemptId}</Mono>
      </ButtonRow>

      {!attemptIsCurrent || !contractIsCurrent || (receipt !== null && !receiptIsAuthoritative) ? (
        <Callout tone="unknown" title="Historical run is incomplete">
          Recorded Work, contract, attempt, and receipt fields remain inspectable below. Missing
          canonical completion metadata, a current durable parent, or prospective assurance prevents
          this run from authorizing enforcement or counting as completion authority.
        </Callout>
      ) : null}

      <ScopeTabs
        value={tab}
        onChange={setTab}
        actionId="runs.universal-detail-tabs"
        options={[
          { key: "summary", label: "Summary", actionId: "runs.tab.summary" },
          { key: "contract", label: "Contract", actionId: "runs.tab.contract" },
          { key: "evidence", label: "Evidence", actionId: "runs.tab.evidence" },
          { key: "checks", label: "Checks", actionId: "runs.tab.checks" },
          { key: "receipt", label: "Receipt", actionId: "runs.tab.receipt" },
          { key: "effects", label: "Effects", actionId: "runs.tab.effects" },
        ]}
      />

      {tab === "summary" ? (
        <>
          <Card>
            <CardContent>
              <Fields>
                <Field label="Work state">{work?.state ?? "Unavailable"}</Field>
                <Field label="Consequence">{work?.consequence ?? "Unavailable"}</Field>
                <Field label="Requester">
                  <Mono>{work?.requestedBy.actorId ?? "Unavailable"}</Mono>
                </Field>
                <Field label="Worker">
                  <Mono>{attempt.worker.actorId}</Mono>
                </Field>
                <Field label="Verifier">
                  <Mono>{receipt?.payload.verifier.actorId ?? "Not assigned"}</Mono>
                </Field>
                <Field label="Adapter">
                  {attempt.adapter.mode} · {attempt.adapter.version}
                </Field>
                <Field label="Input snapshot">
                  <Mono>{attempt.binding.inputSnapshotDigest}</Mono>
                </Field>
                <Field label="Output snapshot">
                  <Mono>{attempt.outputSnapshotDigest ?? "Not submitted"}</Mono>
                </Field>
              </Fields>
            </CardContent>
          </Card>
          {receipt && !receiptIsAuthoritative ? (
            <Callout tone="unknown" title="Recorded receipt is not current assurance">
              Raw verdict {receiptVerdict}, assurance {receipt.payload.assurance},
              and verifier {receipt.payload.verifier.actorId} are historical facts only.
            </Callout>
          ) : attempt.worker.actorId === receipt?.payload.verifier.actorId ? (
            <Callout tone="fail" title="Worker and verifier are not independent">
              This receipt must not authorize a downstream action.
            </Callout>
          ) : receipt ? (
            <Callout tone="neutral" title="Worker and verifier are distinct">
              Worker {attempt.worker.actorId} produced the attempt; verifier{" "}
              {receipt.payload.verifier.actorId} issued the signed verdict.
            </Callout>
          ) : (
            <Callout tone="unknown" title="No signed verdict yet">
              Evidence may be submitted, but no verifier receipt is bound to this run.
            </Callout>
          )}
        </>
      ) : null}

      {tab === "contract" ? (
        contract ? (
          <>
            {!contractIsCurrent ? (
              <Callout tone="unknown" title="Contract completion definition is incomplete">
                This accepted record predates the canonical completion intent and outcome coverage
                model. It remains readable but cannot admit new work.
              </Callout>
            ) : null}
            <Card>
              <CardContent>
                <Fields>
                  <Field label="Contract">
                    <Mono>
                      {contract.content.contractId} · v{contract.content.version}
                    </Mono>
                  </Field>
                  <Field label="Digest">
                    <Mono>{contract.contractDigest}</Mono>
                  </Field>
                  <Field label="Accepted by">
                    <Mono>{contract.acceptance.acceptedBy.actorId}</Mono>
                  </Field>
                  <Field label="Authority">{contract.acceptance.authorityBasis}</Field>
                  <Field label="Deployment mode">{contract.deploymentMode}</Field>
                  <Field label="Bounds">
                    {contract.content.bounds.network} network ·{" "}
                    {contract.content.bounds.allowedMutations.length} allowed mutations
                  </Field>
                </Fields>
              </CardContent>
            </Card>
            <Section
              title="Contract checks"
              note="This is the frozen predicate set for this worker attempt."
            >
              <DataTable
                rows={contract.content.obligations}
                rowKey={(obligation) => obligation.id}
                columns={[
                  {
                    key: "check",
                    header: "Check",
                    render: (obligation) => (
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-foreground">
                          {obligation.predicate.description}
                        </span>
                        <Mono>{obligation.predicate.kind}</Mono>
                      </div>
                    ),
                  },
                  {
                    key: "condition",
                    header: "Success / falsifier",
                    render: (obligation) =>
                      obligation.successCondition ?? obligation.falsifier ?? "Unavailable",
                  },
                  {
                    key: "evidence",
                    header: "Evidence required",
                    render: (obligation) =>
                      obligation.evidence
                        .map(
                          (requirement) =>
                            `${requirement.kind} × ${requirement.minimumCount}${requirement.requiresIndependentCollection ? " · independent" : ""}`,
                        )
                        .join("; "),
                  },
                  {
                    key: "evaluator",
                    header: "Evaluator",
                    render: (obligation) =>
                      `${obligation.evaluator.authority} · ${obligation.evaluator.ref}@${obligation.evaluator.version}`,
                  },
                ]}
              />
            </Section>
          </>
        ) : (
          <PagePending title="Accepted contract unavailable">
            The attempt binding remains visible, but its accepted contract envelope was not
            returned.
          </PagePending>
        )
      ) : null}

      {tab === "evidence" ? (
        receiptDetail.error ? (
          <PageError
            title="Evidence bundle unavailable"
            error={receiptDetail.error}
            onRetry={() => void receiptDetail.mutate()}
          />
        ) : receiptDetail.isLoading && !receiptDetail.data ? (
          <PagePending title="Loading admitted evidence">
            Resolving the content-addressed evidence graph bound to this receipt.
          </PagePending>
        ) : receiptDetail.data ? (
          <>
            <Card>
              <CardContent>
                <Fields>
                  <Field label="Evidence graph">
                    <Mono>{receiptDetail.data.evidence.submissionDigest}</Mono>
                  </Field>
                  <Field label="Submitted by">
                    <Mono>{receiptDetail.data.evidence.submission.submitter.actorId}</Mono>
                  </Field>
                  <Field label="Admitted by">
                    <Mono>{receiptDetail.data.evidence.admittedBy.actorId}</Mono>
                  </Field>
                  <Field label="Worker claim">
                    {receiptDetail.data.evidence.submission.finalClaim?.statement ??
                      "No worker summary supplied"}
                  </Field>
                </Fields>
              </CardContent>
            </Card>
            <DataTable
              rows={receiptDetail.data.evidence.artifacts}
              rowKey={(row) => row.artifact.id}
              columns={[
                {
                  key: "artifact",
                  header: "Artifact",
                  render: (row) => (
                    <div className="flex flex-col gap-1">
                      <span>{row.artifact.id}</span>
                      <Mono>{row.artifact.contentDigest}</Mono>
                    </div>
                  ),
                },
                {
                  key: "kind",
                  header: "Kind",
                  render: (row) => `${row.artifact.kind} · ${row.artifact.mediaType}`,
                },
                {
                  key: "provenance",
                  header: "Provenance",
                  render: (row) =>
                    `${row.artifact.provenance.originActorId} → ${row.artifact.provenance.collector.actorId}`,
                },
                {
                  key: "integrity",
                  header: "Integrity",
                  render: (row) => (
                    <ToneBadge tone={row.bytesVerified ? "pass" : "fail"}>
                      {row.bytesVerified ? "BYTES VERIFIED" : "UNVERIFIED"}
                    </ToneBadge>
                  ),
                },
                {
                  key: "independence",
                  header: "Independence",
                  render: (row) => (
                    <ToneBadge
                      tone={row.independence.classification === "independent" ? "pass" : "unknown"}
                    >
                      {row.independence.classification}
                    </ToneBadge>
                  ),
                },
                {
                  key: "state",
                  header: "State",
                  render: (row) => `${row.artifact.accessibility} · ${row.artifact.freshness}`,
                },
              ]}
            />
            {receiptDetail.data.evidence.submission.gaps.length ? (
              <Callout
                tone="unknown"
                title={`${receiptDetail.data.evidence.submission.gaps.length} evidence gap(s)`}
              >
                {receiptDetail.data.evidence.submission.gaps
                  .map((gap) => `${gap.kind}: ${gap.detail}`)
                  .join("; ")}
              </Callout>
            ) : null}
          </>
        ) : (
          <PagePending title="No admitted evidence">
            This run has no signed receipt that resolves to an admitted evidence graph.
          </PagePending>
        )
      ) : null}

      {tab === "checks" ? (
        receipt ? (
          <DataTable
            rows={receipt.payload.determinations}
            rowKey={(determination) => determination.id}
            columns={[
              {
                key: "check",
                header: "Check",
                render: (determination) =>
                  contract?.content.obligations.find(
                    (obligation) => obligation.id === determination.obligationId,
                  )?.predicate.description ?? determination.obligationId,
              },
              {
                key: "result",
                header: "Result",
                render: (determination) =>
                  receiptIsAuthoritative ? (
                    <Verdict status={determination.status} />
                  ) : (
                    <ToneBadge tone="unknown">
                      {receiptIsRetrospective ? "RETROSPECTIVE" : "INCOMPLETE"} · recorded{" "}
                      {determination.status}
                    </ToneBadge>
                  ),
              },
              {
                key: "authority",
                header: "Authority",
                render: (determination) =>
                  `${determination.authority} · ${determination.evaluatorRef}@${determination.evaluatorVersion}`,
              },
              {
                key: "evidence",
                header: "Evidence",
                render: (determination) => determination.evidenceArtifactIds.length,
              },
              {
                key: "integrity",
                header: "Integrity",
                render: (determination) => (
                  <ToneBadge
                    tone={
                      receiptIsAuthoritative
                        ? determination.deterministicIntegrityPassed
                          ? "pass"
                          : "fail"
                        : "unknown"
                    }
                  >
                    {receiptIsAuthoritative
                      ? determination.deterministicIntegrityPassed
                        ? "PASSED"
                        : "FAILED"
                      : `${receiptIsStructurallyCurrent ? "RETROSPECTIVE" : "INCOMPLETE"} · recorded ${determination.deterministicIntegrityPassed ? "PASSED" : "FAILED"}`}
                  </ToneBadge>
                ),
              },
              {
                key: "rationale",
                header: "Rationale",
                cellClassName: "whitespace-normal",
                render: (determination) => determination.rationale,
              },
            ]}
          />
        ) : (
          <PagePending title="Checks not determined">
            No signed receipt contains authoritative determinations for this run.
          </PagePending>
        )
      ) : null}

      {tab === "receipt" ? (
        receipt ? (
          <Card>
            <CardContent>
              <Fields>
                <Field label="Verdict">
                  {receiptIsAuthoritative ? (
                    <Verdict status={receiptVerdict ?? ""} />
                  ) : (
                    <ToneBadge tone="unknown">
                      {receiptIsStructurallyCurrent ? "RETROSPECTIVE" : "INCOMPLETE"} · recorded{" "}
                      {receiptVerdict}
                    </ToneBadge>
                  )}
                </Field>
                <Field label="Receipt">
                  <Mono>{receipt.payload.id}</Mono>
                </Field>
                <Field label="Payload digest">
                  <Mono>{receipt.payloadDigest}</Mono>
                </Field>
                <Field label="Signed by">
                  <Mono>
                    {receipt.signature.keyId} · {receipt.signature.algorithm}
                  </Mono>
                </Field>
                <Field label="Verifier">
                  <Mono>{receipt.payload.verifier.actorId}</Mono>
                </Field>
                <Field label="Assurance">
                  {receiptIsAuthoritative
                    ? receipt.payload.assurance
                    : `${receiptIsStructurallyCurrent ? "RETROSPECTIVE" : "INCOMPLETE"} · recorded ${receipt.payload.assurance}`}
                </Field>
                <Field label="Replay">
                  {receipt.payload.replay.status} · {receipt.payload.replay.commands.length}{" "}
                  command(s)
                </Field>
                <Field label="Policy">
                  <Mono>
                    {receipt.payload.policy.ref}@{receipt.payload.policy.version}
                  </Mono>
                </Field>
                <Field label="Issued">{formatDateTime(receipt.payload.issuedAt)}</Field>
              </Fields>
            </CardContent>
          </Card>
        ) : (
          <PagePending title="No receipt issued">
            This run has not received an authoritative signed verdict.
          </PagePending>
        )
      ) : null}

      {tab === "effects" ? (
        <>
          <Section title="Reviews">
            <DataTable
              rows={reviews}
              rowKey={(review) => review.request.id}
              emptyState={{
                title: "No reviews",
                description: "No human check, appeal, or dispute is bound to this work.",
              }}
              columns={[
                { key: "review", header: "Review", render: (review) => review.request.reason },
                { key: "kind", header: "Kind", render: (review) => review.request.kind },
                {
                  key: "authority",
                  header: "Authority",
                  render: (review) => review.request.assignedAuthorityActorId ?? "Unassigned",
                },
                {
                  key: "outcome",
                  header: "Outcome",
                  render: (review) => {
                    const recorded = review.resolution?.outcome ?? "OPEN";
                    return currentReviewIds.has(review.request.id) ? (
                      recorded
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
                        <span className="type-subtext text-muted-foreground">
                          Recorded {recorded}
                        </span>
                      </span>
                    );
                  },
                },
              ]}
            />
          </Section>
          <Section title="Downstream effects">
            <Callout tone="neutral" title={`${overview.effects.length} tenant effect attempt(s)`}>
              The current overview exposes effect attempts by decision ID but does not expose the
              decision-to-receipt join. No tenant effect is attributed to this run without that
              binding.
            </Callout>
          </Section>
        </>
      ) : null}
    </PageFrame>
  );
}

function RunDetailBody({ id, onNavigate }: VerificationDetailPageProps) {
  const envCtx = useVerificationEnvOptional();
  const environment: VerificationEnvironment = envCtx?.environment ?? "demo";
  const envReady = envCtx ? envCtx.ready && envCtx.environment !== null : true;
  const me = environmentActor(environment);

  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState(false);

  const runsApi = useApi(
    verificationCanonicalClients.runsList.operation,
    envReady ? { query: { env: environment } } : null,
  );
  const universalApi = useApi(
    universalVerification.overview,
    envReady && environment === "real" ? {} : null,
  );
  const completionProjection = useMemo(
    () => (universalApi.data ? projectUniversalCompletionOverview(universalApi.data) : null),
    [universalApi.data],
  );
  const universalAttempt =
    combineCompletionHistory(
      completionProjection?.current.attempts ?? [],
      completionProjection?.incomplete.attempts ?? [],
      (attempt) => attempt.binding.attemptId,
    ).find((attempt) => attempt.binding.attemptId === decodeURIComponent(id)) ?? null;

  const run = useMemo(() => {
    if (!envReady) return null;
    const decoded = decodeURIComponent(id);
    return (
      (runsApi.data?.items ?? [])
        .map((row) => dtoToRow(row, environment))
        .find((row) => row.id === decoded || stripEnvironmentPrefix(row.id) === decoded) ?? null
    );
  }, [envReady, environment, id, runsApi.data]);

  async function onReplay() {
    if (!run || pending) return;
    setPending(true);
    setNotice(null);
    try {
      if (run.controlIds.length === 0) {
        setNotice("Need control ids to replay this run.");
        return;
      }
      const created = await verificationSingleFlightMutation(
        verificationCanonicalClients.runsCreate.operation,
        {
          query: { env: environment },
          body: {
            actorId: me,
            label: `Replay ${run.label}`,
            controlIds: run.controlIds,
            engineVersion: run.engineVersion,
          },
        },
      );
      setNotice(`Queued ${created.run.label}.`);
      onNavigate(href("runs", created.run.id));
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  const lede = "The full story of one execution: claim, checks, evidence, receipt, and decisions.";

  if (universalAttempt && universalApi.data) {
    return (
      <UniversalRunDetail
        attempt={universalAttempt}
        overview={universalApi.data}
        onNavigate={onNavigate}
      />
    );
  }

  if (!envReady) {
    return (
      <PageFrame page="run" wide title="Run" lede={lede}>
        <PagePending title={`No run loaded for ${id}`}>
          Load the active Environment first.{" "}
          <PageLink onNavigate={onNavigate} to="runs">
            Back to runs
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  if (runsApi.isLoading && !runsApi.data) {
    return (
      <PageFrame page="run" wide title="Run" lede={lede}>
        <PagePending title={`No run loaded for ${id}`}>
          Fetching this run from the WP4a contract.
        </PagePending>
      </PageFrame>
    );
  }

  if (runsApi.error && !runsApi.data) {
    return (
      <PageFrame page="run" wide title="Run" lede={lede}>
        <PageError
          title={`No run loaded for ${id}`}
          error={runsApi.error}
          onRetry={() => void runsApi.mutate()}
        />
        <ButtonRow>
          <ActionButton actionId={RUNS_REPLAY_ACTION} data-action-id={RUNS_REPLAY_ACTION} disabled>
            Replay
          </ActionButton>
          <ActionButton
            actionId={RUNS_OPEN_RECEIPT_ACTION}
            data-action-id={RUNS_OPEN_RECEIPT_ACTION}
            variant="primary"
            disabled
          >
            Open receipt
          </ActionButton>
        </ButtonRow>
      </PageFrame>
    );
  }

  if (!run) {
    return (
      <PageFrame page="run" wide title="Run" lede={lede}>
        <PagePending title={`No run loaded for ${id}`}>
          Run detail is the chain from evidence hashes to the receipt record.{" "}
          <PageLink onNavigate={onNavigate} to="runs">
            Back to runs
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  const replayDisabled = pending
    ? "Another run action is in flight."
    : run.lifecycle !== "completed"
      ? "Replay needs a completed run."
      : run.controlIds.length === 0
        ? "Need control ids to replay."
        : undefined;
  const receiptDisabled = run.receiptId ? undefined : "This run issued no receipt.";

  return (
    <PageFrame
      page="run"
      wide
      title={run.label}
      lede={
        run.startedAt
          ? `Started ${formatDateTime(run.startedAt)} by ${run.createdByActorId}. Engine ${run.engineVersion}.`
          : lede
      }
      actions={
        <>
          <ActionButton
            actionId={RUNS_REPLAY_ACTION}
            disabled={Boolean(replayDisabled)}
            reason={replayDisabled}
            onClick={() => void onReplay()}
          >
            Replay
          </ActionButton>
          <ActionButton
            actionId={RUNS_OPEN_RECEIPT_ACTION}
            variant="primary"
            disabled={Boolean(receiptDisabled)}
            reason={receiptDisabled}
            onClick={() => {
              if (run.receiptId) onNavigate(href("receipts", run.receiptId));
            }}
          >
            Open receipt
          </ActionButton>
        </>
      }
    >
      {notice ? (
        <ReadOnlyFailureCallout
          notice={notice}
          title="Action result"
          actionId="runs.action-status"
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <RunVerdict status={run.aggregate} />
        <ToneBadge tone={run.lifecycle === "completed" ? "pass" : "unknown"}>
          {run.lifecycle}
        </ToneBadge>
        <Mono>{run.id}</Mono>
      </div>

      {!run.receiptId ? (
        <Callout tone="unknown" title="This run issued no receipt.">
          A receipt requires every check to carry evidence. Open receipt stays visible and disabled.
        </Callout>
      ) : null}

      <Card>
        <CardContent>
          <Fields>
            <Field label="Controls">
              <Mono>{run.controlIds.length}</Mono>
            </Field>
            <Field label="Evidence">
              <Mono>{run.evidenceArtifactIds.length} artifacts</Mono>
            </Field>
            <Field label="Completed">
              {run.completedAt ? formatDateTime(run.completedAt) : "—"}
            </Field>
            {run.receiptId ? (
              <Field label="Receipt">
                <div className="flex flex-wrap items-center gap-2">
                  <Mono>{run.receiptId}</Mono>
                  <ActionButton
                    actionId={RUNS_OPEN_RECEIPT_ACTION}
                    variant="ghost"
                    onClick={() => onNavigate(href("receipts", run.receiptId!))}
                  >
                    Open
                  </ActionButton>
                </div>
              </Field>
            ) : null}
          </Fields>
        </CardContent>
      </Card>

      <p>
        <PageLink onNavigate={onNavigate} to="runs">
          Back to runs
        </PageLink>
      </p>
    </PageFrame>
  );
}
